import { apiClient } from './client';

// ==================== TYPES ====================

export interface LocationPricing {
  location_id: string;
  location_name: string;
  discounted_price?: number;
  discount_percentage?: number;
}

export interface DiscountTier {
  id?: string;
  name: string;
  threshold_min: number;
  threshold_max: number;
  lodging_included: boolean;
  sort_order: number;
  location_pricing: LocationPricing[];
  created_at?: Date;
  updated_at?: Date;
}

export interface GetDiscountTiersResponse {
  planType: 'weekday' | 'weekend';
  tiers: DiscountTier[];
  count: number;
}

export interface UpdateDiscountTiersResponse {
  message: string;
  planType: 'weekday' | 'weekend';
  tiers: DiscountTier[];
  count: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// Public threshold data (with main house discount info)
export interface DiscountTierThreshold {
  id: string;
  name: string;
  threshold_min: number;
  threshold_max: number;
  lodging_included: boolean;
  sort_order: number;
  main_house_discount?: {
    discounted_price?: number;
    discount_percentage?: number;
  };
}

export interface GetDiscountTierThresholdsResponse {
  planType: 'weekday' | 'weekend';
  thresholds: DiscountTierThreshold[];
  count: number;
}

// ==================== API FUNCTIONS ====================

/**
 * Get discount tier thresholds (public - no auth required)
 * Returns only threshold ranges without pricing data
 */
export async function getDiscountTierThresholds(
  planType: 'weekday' | 'weekend'
): Promise<GetDiscountTierThresholdsResponse> {
  const response = await apiClient.get<GetDiscountTierThresholdsResponse>(
    `/admin/settings/discount-tier-thresholds/${planType}`
  );
  return response.data;
}

/**
 * Sanitize tiers by removing fields that shouldn't be sent to backend
 * (MongoDB internal fields and timestamps)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sanitizeTiersForBackend(tiers: DiscountTier[]): any[] {
  return tiers.map(tier => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    const { created_at, updated_at, ...sanitizedTier } = tier as any;

    // Remove _id if it exists (MongoDB internal field)
    if ('_id' in sanitizedTier) {
      delete sanitizedTier._id;
    }

    // Also sanitize location_pricing array - remove _id from each location pricing entry
    if (sanitizedTier.location_pricing && Array.isArray(sanitizedTier.location_pricing)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sanitizedTier.location_pricing = sanitizedTier.location_pricing.map((lp: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { _id, ...sanitizedLp } = lp;
        return sanitizedLp;
      });
    }

    return sanitizedTier;
  });
}

/**
 * Get discount tiers for a specific plan type (weekday or weekend)
 */
export async function getDiscountTiers(
  planType: 'weekday' | 'weekend'
): Promise<GetDiscountTiersResponse> {
  const response = await apiClient.get<GetDiscountTiersResponse>(
    `/admin/settings/discount-tiers/${planType}`
  );
  return response.data;
}

/**
 * Update discount tiers for a specific plan type
 * This replaces ALL tiers for the given plan type
 */
export async function updateDiscountTiers(
  planType: 'weekday' | 'weekend',
  tiers: DiscountTier[]
): Promise<UpdateDiscountTiersResponse> {
  // Strip timestamp fields before sending to backend
  const sanitizedTiers = sanitizeTiersForBackend(tiers);

  const response = await apiClient.patch<UpdateDiscountTiersResponse>(
    `/admin/settings/discount-tiers/${planType}`,
    { tiers: sanitizedTiers }
  );
  return response.data;
}

/**
 * Validate discount tiers without saving
 * Returns validation results with errors if any
 */
export async function validateDiscountTiers(
  planType: 'weekday' | 'weekend',
  tiers: DiscountTier[]
): Promise<ValidationResult> {
  const response = await apiClient.post<ValidationResult>(
    `/admin/settings/discount-tiers/${planType}/validate`,
    { tiers }
  );
  return response.data;
}

// ==================== FEATURE FLAGS ====================

export interface FeatureFlags {
  client_portal_enabled: boolean;
  /** Native screens inside the portal (off: the legacy tab) */
  invitados_v2_enabled: boolean;
  contrato_v2_enabled: boolean;
  pagos_v2_enabled: boolean;
  /** Client UI new events start with (true: v2, false: legacy); existing events keep theirs */
  client_ui_v2_default: boolean;
  /** Probability (0-1) that a new visitor gets the instant quote arm */
  instant_quote_split: number;
  /** Chromium renders contract/receipt/cancellation PDFs in single-process mode (less memory) */
  pdf_single_process: boolean;
  /** Landing hero shows the weekday "desde" price for 50 guests */
  landing_price_enabled: boolean;
}

export interface FeatureFlagState {
  /** What the app is using right now */
  flags: FeatureFlags;
  /** Values set from admin; null where the env value applies */
  overrides: { [K in keyof FeatureFlags]: FeatureFlags[K] | null };
  /** Values from the server environment, used when there is no override */
  env: FeatureFlags;
}

export async function getFeatureFlagState(): Promise<FeatureFlagState> {
  const response = await apiClient.get<FeatureFlagState>('/admin/settings/feature-flags');
  return response.data;
}

/** `null` clears an override so the env value applies again. */
export async function updateFeatureFlags(
  patch: Partial<{ [K in keyof FeatureFlags]: FeatureFlags[K] | null }>,
): Promise<FeatureFlagState> {
  const response = await apiClient.patch<FeatureFlagState>('/admin/settings/feature-flags', patch);
  return response.data;
}

export type PublicFlags = Pick<
  FeatureFlags,
  | 'client_portal_enabled'
  | 'invitados_v2_enabled'
  | 'contrato_v2_enabled'
  | 'pagos_v2_enabled'
  | 'landing_price_enabled'
>;

/** Public: the flags the site needs before anyone logs in. */
export async function getPublicFlags(): Promise<PublicFlags> {
  const response = await apiClient.get<PublicFlags>(
    '/admin/settings/public-flags',
  );
  return response.data;
}
